import type { CheckoutCartAddress } from '~~/layers/base/types/checkout/setShippingAddressOnGuestCart'
import { formatAddressAddressee } from '~~/layers/base/utils/crane/address'
import { formatAddressLines } from '~~/layers/base/utils/formatAddressLine'
import { normalizeAddressCountryCode } from '~~/layers/base/utils/normalizeAddressCountryCode'

/** `public.profile` first / last name columns (not Better Auth `user.name`). */
export type ProfileNameColumns = {
    firstName?: string | null
    lastName?: string | null
}

/**
 * Joined profile first / last for display; null when both columns are empty.
 * Unlike `formatProfileDisplayName`, identical first / last names are NOT deduped,
 * so a row of `{ firstName: 'Bob', lastName: 'Bob' }` renders as `'Bob Bob'`.
 */
export function formatProfileNameJoined(
    profile: ProfileNameColumns | null | undefined
): string | null {
    if (!profile) {
        return null
    }
    const parts = [profile.firstName?.trim(), profile.lastName?.trim()].filter(Boolean)
    return parts.length ? parts.join(' ') : null
}

export type AddressLikeForCheckout = {
    prefix?: string | null
    suffix?: string | null
    first_name?: string | null
    last_name?: string | null
    street_address_1?: string | null
    street_address_2?: string | null
    city?: string | null
    state?: string | null
    zip_code?: string | null
    country?: string | null
    country_code?: string | null
}

/** `public.profile` first / last name columns (not Better Auth `user.name`). */
export type ProfileNameFields = {
    firstName?: string | null
    lastName?: string | null
}

export type MapAddressLikeToCheckoutCartAddressOptions = {
    /** Contact display name for recipient shipping when the address has no addressee. */
    nameFallback?: string | null
    /** Profile first / last for return billing when the address has no addressee. */
    profileNameFallback?: ProfileNameFields | null
}

/** True when the address row carries its own addressee (overrides contact / profile name). */
export function addressHasOwnAddressee(row: AddressLikeForCheckout): boolean {
    return Boolean(
        row.prefix?.trim() || row.first_name?.trim() || row.last_name?.trim() || row.suffix?.trim()
    )
}

/**
 * Return-address display fallback: profile first/last when set, else Better Auth session
 * `user.name` (same order as `ReturnAddressDrawerHost` identity card).
 */
export function resolveReturnAddressFallbackName(
    profile: ProfileNameFields | null | undefined,
    sessionUserName?: string | null
): string | null {
    return formatProfileDisplayName(profile) ?? sessionUserName?.trim() ?? null
}

/** Display line from profile columns; partial names allowed; null when both empty. */
export function formatProfileDisplayName(
    profile: ProfileNameFields | null | undefined
): string | null {
    if (!profile) {
        return null
    }
    const first = profile.firstName?.trim() ?? ''
    const last = profile.lastName?.trim() ?? ''
    if (!first && !last) {
        return null
    }
    if (first && last && first === last) {
        return first
    }
    return [first, last].filter(Boolean).join(' ')
}

/**
 * ACCS firstname / lastname from profile columns. No Better Auth session name.
 * Duplicates a single present field for ACCS wire only (checkout display dedupes).
 */
export function profilePersonNamesForCheckout(
    profile: ProfileNameFields | null | undefined
): { firstname: string; lastname: string } | null {
    if (!profile) {
        return null
    }
    const first = profile.firstName?.trim() ?? ''
    const last = profile.lastName?.trim() ?? ''
    if (!first && !last) {
        return null
    }
    if (first && last) {
        return { firstname: first, lastname: last }
    }
    if (first) {
        return { firstname: first, lastname: first }
    }
    return { firstname: last, lastname: last }
}

/**
 * Addressee line for envelope / picker display: address fields when set, else fallback name.
 */
export function resolveAddressAddresseeLine(
    row: AddressLikeForCheckout,
    fallbackName?: string | null
): string | null {
    if (addressHasOwnAddressee(row)) {
        return formatAddressAddressee(row) || null
    }
    const fallback = fallbackName?.trim()
    return fallback || null
}

/** Envelope / picker lines: addressee when overridden, else fallback name, then postal lines. */
export function buildAddressDisplayLines(
    row: AddressLikeForCheckout,
    fallbackName?: string | null
): string[] {
    const addressee = resolveAddressAddresseeLine(row, fallbackName)
    const postal = formatAddressLines(row)
    return addressee ? [addressee, ...postal] : postal
}

/** Contact display name → ACCS firstname + lastname (single token duplicated for ACCS wire). */
export function splitPersonName(name: string): { firstname: string; lastname: string } {
    const parts = name.trim().split(/\s+/).filter(Boolean)
    if (parts.length === 0) {
        return { firstname: '', lastname: '' }
    }
    if (parts.length === 1) {
        return { firstname: parts[0]!, lastname: parts[0]! }
    }
    return { firstname: parts[0]!, lastname: parts.slice(1).join(' ') }
}

/**
 * Resolves ACCS `firstname` / `lastname` for checkout cart mapping (TK-13090).
 * Address addressee overrides fallback; profile / contact fallbacks never mix with overrides.
 */
export function resolveCheckoutCartPersonNames(
    row: AddressLikeForCheckout,
    options?: MapAddressLikeToCheckoutCartAddressOptions
): { firstname: string; lastname: string } {
    if (addressHasOwnAddressee(row)) {
        let firstname = row.first_name?.trim() ?? ''
        let lastname = row.last_name?.trim() ?? ''

        if (!firstname && !lastname) {
            const addressee = formatAddressAddressee(row)
            if (addressee) {
                return splitPersonName(addressee)
            }
        }

        // ACCS requires both fields; duplicate single-token overrides for wire only (display dedupes).
        if (firstname && !lastname) {
            lastname = firstname
        } else if (!firstname && lastname) {
            firstname = lastname
        }

        return { firstname, lastname }
    }

    const profileNames = profilePersonNamesForCheckout(options?.profileNameFallback)
    if (profileNames) {
        return profileNames
    }

    if (options?.nameFallback?.trim()) {
        return splitPersonName(options.nameFallback)
    }

    return { firstname: '', lastname: '' }
}

/**
 * Maps a DB/contact/return address-shaped row to ACCS guest-cart address input.
 * Returns null when street/city/name cannot be resolved (e.g. raw-only rows).
 *
 * Omits `telephone` — Capacitor addresses have no phone column. The billing/shipping atomics
 * apply `resolveAccsCartAddressTelephone` before sending to ACCS.
 */
export function mapAddressLikeToCheckoutCartAddress(
    row: AddressLikeForCheckout,
    options?: MapAddressLikeToCheckoutCartAddressOptions
): CheckoutCartAddress | null {
    const { firstname, lastname } = resolveCheckoutCartPersonNames(row, options)

    const street1 = row.street_address_1?.trim()
    const city = row.city?.trim()
    const countryCode = normalizeAddressCountryCode(row.country_code ?? row.country)

    if (!firstname || !lastname || !street1 || !city || !countryCode) {
        return null
    }

    const street = [street1, row.street_address_2?.trim()].filter((line): line is string =>
        Boolean(line)
    )

    return {
        firstname,
        lastname,
        street,
        city,
        region: row.state?.trim() || undefined,
        postcode: row.zip_code?.trim() || undefined,
        country_code: countryCode,
    }
}
